import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SharedServiceService } from './shared-service.service';
import { saveAs } from 'file-saver';

interface PropertyRow {
  property: string;
  method: string;
  unit: string;
  decimals: number;
}

@Injectable({
  providedIn: 'root'
})
export class PredictionExportService {

  private readonly propertyRows: PropertyRow[] = [
    { property: 'KV100', method: 'ASTM D445', unit: 'cSt', decimals: 2 },
    { property: 'KV40', method: 'ASTM D445', unit: 'cSt', decimals: 2 },
    { property: 'VI', method: 'ASTM D2270', unit: '', decimals: 0 },
    { property: 'KV20', method: 'ASTM D341', unit: 'cSt', decimals: 2 },
    { property: 'HTHS 150C', method: 'ASTM D4683', unit: 'cP', decimals: 2 },
    { property: 'HTHS 100C', method: 'ASTM D4683', unit: 'cP', decimals: 2 },
    { property: 'KV100 (30 Cycles Sheared)', method: 'ASTM D7109', unit: 'cSt', decimals: 2 },
    { property: 'KV100 (90 Cycles Sheared)', method: 'ASTM D7109', unit: 'cSt', decimals: 2 },
    { property: 'CCS', method: 'ASTM D5293', unit: '', decimals: 0 },
    { property: '-10C (25W)', method: '', unit: 'cP', decimals: 0 },
    { property: '-15C (20W)', method: '', unit: 'cP', decimals: 0 },
    { property: '-20C (15W)', method: '', unit: 'cP', decimals: 0 },
    { property: '-25C (10W)', method: '', unit: 'cP', decimals: 0 },
    { property: '-30C (5W)', method: '', unit: 'cP', decimals: 0 },
    { property: '-35C (0W)', method: '', unit: 'cP', decimals: 0 },
    { property: 'NOACK', method: 'ASTM D5800', unit: '%', decimals: 1 },
    { property: 'BOBV (100C)', method: 'ASTM D445', unit: 'cSt', decimals: 2 },
    { property: 'BOBV (40C)', method: 'ASTM D445', unit: 'cSt', decimals: 2 },
    { property: 'BOB VI', method: 'ASTM D2270', unit: 'cSt', decimals: 0 },
    { property: 'BOB Saturates', method: 'ASTM D2007', unit: '%', decimals: 2 },
    { property: 'BOB Sulfur', method: 'ASTM D2622', unit: '%', decimals: 4 },
  ];

  constructor(
    private sharedService: SharedServiceService
  ) {}

  async exportPredictions(
    blend1Predictions: string[],
    blend2Predictions: string[],
    j300_1: string[],
    j300_2: string[]
  ): Promise<void> {
    // 1. Build prediction results section (columns A-G)
    const predSection = this.buildPredictionSection(blend1Predictions, blend2Predictions, j300_1, j300_2);

    // 2. Fetch package data and build package sections
    const packageSections = await this.buildPackageSections();

    // 3. Build model inputs section (Base Oil, VM, PPD)
    const modelInputsSection = await this.buildModelInputsSection();

    // DEBUG: Remove after confirming data flow
    console.group('[ExportPrediction] Debug Data');
    console.log('blend1Predictions:', blend1Predictions);
    console.log('blend2Predictions:', blend2Predictions);
    console.log('packageSections.pkg1:', packageSections.pkg1);
    console.log('packageSections.pkg2:', packageSections.pkg2);
    console.log('modelInputsSection:', modelInputsSection);
    console.groupEnd();

    // 4. Compose horizontal layout: [PredResults] | gap | [Pkg1] | gap | [Pkg2] | gap | [ModelInputs]
    const maxRows = Math.max(
      predSection.length,
      packageSections.pkg1.length,
      packageSections.pkg2.length,
      modelInputsSection.length
    );

    const sheet: any[][] = [];
    for (let r = 0; r < maxRows; r++) {
      const predRow = predSection[r] || this.emptyArray(7);
      const pkg1Row = packageSections.pkg1[r] || this.emptyArray(3);
      const pkg2Row = packageSections.pkg2[r] || this.emptyArray(3);
      const miRow = modelInputsSection[r] || this.emptyArray(4);

      sheet.push([
        ...predRow,
        '',  // gap
        ...pkg1Row,
        '',  // gap
        ...pkg2Row,
        '',  // gap
        ...miRow,
      ]);
    }

    // 5. Generate CSV data
    const csv = sheet
      .map((row) =>
        row
          .map((cell) => this.escapeCsvCell(cell))
          .join(',')
      )
      .join('\r\n');

    // 6. Save file
    const blob = new Blob([csv], {
      type: 'text/csv;charset=UTF-8'
    });

    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const yyyy = now.getFullYear();
    const HH = String(now.getHours()).padStart(2, '0');
    const MM = String(now.getMinutes()).padStart(2, '0');
    const SS = String(now.getSeconds()).padStart(2, '0');
    saveAs(blob, `Predictions_export_${mm}${dd}${yyyy}_${HH}${MM}${SS}.csv`);
  }

  private buildPredictionSection(
    blend1: string[],
    blend2: string[],
    j300_1: string[],
    j300_2: string[]
  ): any[][] {
    const rows: any[][] = [];

    // Header row
    rows.push(['Properties', 'Method', 'Unit', 'Blend 1', 'J300', 'Blend 2', 'J300']);

    // Data rows (21 properties)
    this.propertyRows.forEach((prop, i) => {
      rows.push([
        prop.property,
        prop.method,
        prop.unit,
        blend1[i] ?? '',
        j300_1[i] ?? '',
        blend2[i] ?? '',
        j300_2[i] ?? '',
      ]);
    });

    return rows;
  }

  private async buildPackageSections(): Promise<{ pkg1: any[][]; pkg2: any[][] }> {
    // retrievedPackageData$ structure per package:
    // [packageName, treatWt, dilPercent, additiveRecords[]]
    // where additiveRecords = [{Additive, 'Wt% in Finished Oil', ...}, ...]
    const retrievedPkgs = await firstValueFrom(this.sharedService.retrievedPackageData$);

    // Also check if adjusted rows exist (populated when treat weight is changed)
    const pkg1p1 = await firstValueFrom(this.sharedService.package_1_pred_1$);
    const pkg1p2 = await firstValueFrom(this.sharedService.package_1_pred_2$);
    const pkg2p1 = await firstValueFrom(this.sharedService.package_2_pred_1$);
    const pkg2p2 = await firstValueFrom(this.sharedService.package_2_pred_2$);

    // Per-blend package totals (Package Wt%). These are recomputed by the UI
    // whenever a DI component treat changes, so they can differ between blends.
    const total1p1 = await firstValueFrom(this.sharedService.total_package_1_pred_1$);
    const total1p2 = await firstValueFrom(this.sharedService.total_package_1_pred_2$);
    const total2p1 = await firstValueFrom(this.sharedService.total_package_2_pred_1$);
    const total2p2 = await firstValueFrom(this.sharedService.total_package_2_pred_2$);

    // DEBUG: Remove after confirming data flow
    console.group('[ExportPrediction] Raw package data');
    console.log('retrievedPkgs:', retrievedPkgs);
    console.log('pkg1p1 (adjusted):', pkg1p1);
    console.log('pkg1p2 (adjusted):', pkg1p2);
    console.groupEnd();

    const pkg1 = this.buildSinglePackageSection(retrievedPkgs?.[0], pkg1p1, pkg1p2, total1p1, total1p2);
    const pkg2 = this.buildSinglePackageSection(retrievedPkgs?.[1], pkg2p1, pkg2p2, total2p1, total2p2);

    return { pkg1, pkg2 };
  }

  private buildSinglePackageSection(
    pkgData: any,
    adjustedP1: any[],
    adjustedP2: any[],
    totalP1?: number,
    totalP2?: number
  ): any[][] {
    const rows: any[][] = [];

    if (!pkgData || !Array.isArray(pkgData)) {
      return rows;
    }

    // pkgData = [packageName, treatWt, dilPercent, additiveRecords[]]
    const pkgName = pkgData[0] ?? '';
    const treatWt = pkgData[1];
    const dilPercent = pkgData[2];
    const baseAdditives: any[] = pkgData[3] ?? [];

    if (!pkgName && baseAdditives.length === 0) {
      return rows;
    }

    // Header
    rows.push(['Additive', 'Blend 1 Wt%', 'Blend 2 Wt%']);

    // Package name
    if (pkgName) {
      rows.push(['Package: ' + pkgName, '', '']);
    }

    // Package treat weight (per blend). Prefer the UI-recomputed per-blend totals
    // so DI treat changes are reflected; fall back to the static package treat weight.
    const wtP1 = totalP1 != null && Number(totalP1) > 0 ? totalP1 : treatWt;
    const wtP2 = totalP2 != null && Number(totalP2) > 0 ? totalP2 : treatWt;
    if (treatWt != null || (totalP1 != null && totalP2 != null)) {
      rows.push(['Package Wt%', this.safeFixed(wtP1, 2), this.safeFixed(wtP2, 2)]);
    }

    // Additive rows: use adjusted data if available (from treat weight changes),
    // otherwise fall back to base additives from retrievedPackageData
    if (adjustedP1.length > 0 || adjustedP2.length > 0) {
      // Adjusted data exists (treat weight was changed) - merge Pred1/Pred2
      const merged = this.mergeAdditiveRows(adjustedP1, adjustedP2);
      merged.forEach(row => {
        rows.push([row.name, this.safeFixed(row.wtP1, 3), this.safeFixed(row.wtP2, 3)]);
      });
    } else if (baseAdditives.length > 0) {
      // Use base additives from initial package selection (same for both blends)
      baseAdditives.forEach((row: any) => {
        const wtFO = row['Wt% in Finished Oil'] ?? row.wtInFO ?? 0;
        rows.push([
          row.Additive ?? row.component ?? '',
          this.safeFixed(wtFO, 3),
          this.safeFixed(wtFO, 3),
        ]);
      });
    }

    // Diluent oil row
    if (dilPercent != null && Number(dilPercent) > 0) {
      rows.push(['Dil Oil', this.safeFixed(dilPercent, 2), this.safeFixed(dilPercent, 2)]);
    }

    return rows;
  }

  private mergeAdditiveRows(pred1: any[], pred2: any[]): { name: string; wtP1: number; wtP2: number }[] {
    const map = new Map<string, { wtP1: number; wtP2: number }>();

    if (pred1 && pred1.length > 0) {
      pred1.forEach(row => {
        const name = row.component || row.Additive || '';
        if (name) {
          map.set(name, { wtP1: row.wtInFO ?? row['Wt% in Finished Oil'] ?? 0, wtP2: 0 });
        }
      });
    }

    if (pred2 && pred2.length > 0) {
      pred2.forEach(row => {
        const name = row.component || row.Additive || '';
        if (name) {
          const existing = map.get(name) || { wtP1: 0, wtP2: 0 };
          existing.wtP2 = row.wtInFO ?? row['Wt% in Finished Oil'] ?? 0;
          map.set(name, existing);
        }
      });
    }

    return Array.from(map.entries()).map(([name, vals]) => ({
      name,
      ...vals
    }));
  }

  private safeFixed(value: any, decimals: number): string | number {
    if (value == null || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return parseFloat(num.toFixed(decimals));
  }

  private async buildModelInputsSection(): Promise<any[][]> {
    const rows: any[][] = [];

    const baseOilRows = await firstValueFrom(this.sharedService.baseOilSelectedData$);
    const vmRows = await firstValueFrom(this.sharedService.vmSelectedData$);
    const ppdRows = await firstValueFrom(this.sharedService.ppdSelectedData$);

    // Header
    rows.push(['Component', 'Blend 1', 'Blend 2', 'Details']);

    // Base Oil section
    const validBaseOils = (baseOilRows || []).filter((r: any) => r.oil);
    if (validBaseOils.length > 0) {
      rows.push(['--- Base Oil ---', '', '', '']);
      validBaseOils.forEach((row: any) => {
        rows.push([
          row.oil,
          this.safeFixed(row.ratio1, 2),
          this.safeFixed(row.ratio2, 2),
          row.batch || ''
        ]);
      });
    }

    // VM section
    const validVMs = (vmRows || []).filter((r: any) => r.selectedValue);
    if (validVMs.length > 0) {
      rows.push(['--- Viscosity Modifier ---', '', '', '']);
      validVMs.forEach((row: any) => {
        rows.push([
          row.selectedValue,
          this.safeFixed(row.treat1, 2),
          this.safeFixed(row.treat2, 2),
          ''
        ]);
      });
    }

    // PPD section
    const validPPDs = (ppdRows || []).filter((r: any) => r.ppd);
    if (validPPDs.length > 0) {
      rows.push(['--- Pour Point Depressant ---', '', '', '']);
      validPPDs.forEach((row: any) => {
        rows.push([
          row.ppd,
          this.safeFixed(row.treat1, 2),
          this.safeFixed(row.treat2, 2),
          ''
        ]);
      });
    }

    return rows;
  }

  private emptyArray(length: number): any[] {
    return new Array(length).fill('');
  }

  private escapeCsvCell(value: any): string {
    if (value == null) return '';
    const cell = String(value);
    if (/[",\r\n]/.test(cell)) {
      return `"${cell.replace(/"/g, '""')}"`;
    }
    return cell;
  }
}
